import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { SignalIndicator } from '~/components';
import * as lib from '../../dependencies';

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    width: 150,
    height: 191,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

export default function RTCStats({ callId }) {
  // SELECTORS
  const calls = useSelector(state => state.callCurrent);
  const callsMemo = useMemo(() => calls && calls.data, [calls]);

  // STATES
  const [rtt, setRtt] = useState(null);
  const [packetsLostValue, setPacketsLost] = useState(null);
  const [jitterValue, setJitter] = useState(null);
  const [packetsSentValue, setPacketsSent] = useState(null);
  const [codecValue, setCodec] = useState(null);
  const [mosResult, setMosResult] = useState(null);

  function getMOS(latency, jitter, packetLost) {
    const effective_lantency = latency + jitter * 2 + 10;
    let R;
    let MOS;

    if (effective_lantency < 160) {
      R = 93.2 - effective_lantency / 40;
    } else {
      R = 93.2 - (effective_lantency - 120) / 10;
    }

    R -= 2.5 * packetLost;

    if (R < 0) {
      MOS = 1.0;
    } else if (R >= 0 && R < 100) {
      MOS = 1 + 0.035 * R + 0.000007 * R * (R - 60) * (100 - R);
    } else {
      MOS = 4.5;
    }

    return MOS;
  }

  const setRTCStats = useCallback(
    async pc => {
      // Get the stats for the sender media track.
      const stats = await pc.getSenders()[0]?.getStats();

      if (stats) {
        stats.forEach(report => {
          if (report.type === 'outbound-rtp') {
            setPacketsSent(report.packetsSent);
          } else if (report.type === 'remote-inbound-rtp') {
            setRtt(report.roundTripTime * 1000);
            setJitter(report.jitter * 1000);
            setPacketsLost(report.packetsLost);
          } else if (report.type === 'codec') {
            const codecName = report.mimeType.split('/')[1];
            setCodec(codecName.charAt(0).toUpperCase() + codecName.slice(1));
          }
        });

        if (
          rtt !== null &&
          jitterValue !== null &&
          packetsLostValue !== null &&
          packetsSentValue !== null
        ) {
          const packetsLossPercent =
            (packetsLostValue * 100) / packetsSentValue;
          const mos = getMOS(rtt, jitterValue, packetsLossPercent);
          setMosResult(mos);
        }
      }
    },
    [jitterValue, packetsLostValue, packetsSentValue, rtt]
  );

  // EFFECTS
  useEffect(() => {
    let interval = null;
    if (callId) {
      const call = callsMemo.filter(e => e.id === callId)[0];
      interval = setInterval(() => {
        if (call.answered) {
          const pc = call.session.sessionDescriptionHandler?.peerConnection;
          if (pc) {
            setRTCStats(pc);
          }
        }
      }, 3000);
    }

    return () => clearInterval(interval);
  }, [callId, callsMemo, setRTCStats]);

  return (
    <div>
      <HtmlTooltip
        title={
          <>
            <p style={{ fontSize: 14, textAlign: 'center' }}>
              <b>Call metrics</b>
            </p>
            <p>
              <b>Latency:</b> {mosResult && rtt ? `${rtt.toFixed(2)}ms` : '-'}
            </p>
            <p>
              <b>Jitter:</b>{' '}
              {mosResult && jitterValue ? `${jitterValue.toFixed(2)}ms` : '-'}
            </p>
            <p>
              <b>Packet loss:</b>{' '}
              {mosResult !== null && packetsLostValue !== null
                ? packetsLostValue
                : '-'}
            </p>
            <p>
              <b>Codec:</b> {(mosResult !== null && codecValue) || '-'}
            </p>
            <p>
              <b>MOS:</b> {mosResult && mosResult.toFixed(2)}
            </p>
          </>
        }
        arrow>
        <div>
          <SignalIndicator score={Number(mosResult)} />
        </div>
      </HtmlTooltip>
    </div>
  );
}

RTCStats.propTypes = {
  callId: lib.PropTypes.string.isRequired,
};
